import type { RewardScheduleConfig } from './schema';

// Typed seed for the contingency-management reward schedules — the single
// source of truth for the `reward_schedules` rows: their frozen ids and their
// full config. Two writers render it and neither may diverge from it:
//   - the data migration under `migrations/*_reward_schedules_provision.sql`
//     (rendered by `buildRewardScheduleStatements` in ./seed-statements.ts),
//     which provisions every environment production included, and
//   - `seed.sql` (executed by `pnpm db:seed` on dev/preview), whose upserts
//     mirror the same rows so a fresh preview — where the migration runs before
//     the programme catalog is seeded — still gets them.
// Both write identical config with `ON CONFLICT … DO UPDATE SET config =
// EXCLUDED.config`, so whichever runs second converges instead of overwriting.
// Contract tests pin both renderings to this constant.
//
// The earn rules are the backend2 production CMv3 values as of September 2026
// (`contingency_management_tier_settings.rewardPointingSystem`: firstDailyCheckIn
// 150, attendedAppointment 2000, firstAttendedAppointment 7500 on both tiers).
// backend2's fourth earn behaviour, verifiedTestResult (1000), is carried only by
// the four non-tobacco substance programmes, as backend2's `non_tobacco_only`
// tier carries it; it is earned when a care-team member locks a qualifying UDS or
// breathalyzer form in Healthie. Only the programmes
// that run a CM loop get a schedule; mood does not. `programKey` maps to a seeded
// `program_definitions.key` at apply time (resolved to `program_definition_id`).
//
// HOW TO CHANGE THIS CONSTANT: edit it here, let the seed.sql parity test drive
// the seed.sql rows to match, then add a NEW `--custom` migration in the same
// family (`pnpm db:generate --custom --name reward_schedules_provision`) whose
// body is `renderRewardScheduleMigrationBody()`. The pin test compares only the
// newest migration of the family with the renderer, so older ones stay as
// history. Never edit an already-applied migration: Drizzle will not re-run it,
// and dev/prod would silently keep the old config while the tests stay green.
//
// `id` is a frozen UUIDv7 literal, transcribed from the historical seed rows.
// It is load-bearing (deployed rows carry it) and MUST NOT be regenerated —
// `newId()` is time/random based, so a regenerated value would desynchronise
// every environment from the committed one.
//
// A completed appointment credits `first_attended_appointment` (7500) once per
// member, then `attended_appointment` (2000) thereafter. The daily cap stays on
// the schedule-level `caps.daily_points_max` here (the expand-safe shape the
// previous release's strict reader still parses); the new reader clamps only the
// check-in behaviour by it, so the appointment earns are never clipped to 150.
// Moving the cap onto a per-earn-rule `daily_max` is the contract-phase follow-up
// (only safe once every reader supports it).
//
// `max_yearly_redemption` is the redemption-side yearly cap, in points (100 pts =
// $1, the same scale as product prices): 15000 for tobacco and for the two
// behavioural programmes (gambling, gaming); 40000 for the four other
// substance programmes. The substance values are the backend2 production
// values as of September 2026 (`contingency_management_tier_settings.
// maxYearlyRedemption` for the `tobacco_only` / `non_tobacco_only` tiers);
// backend2 had no behavioural tiers, so gambling and gaming take the tobacco
// cap by product decision. Submit is fail-closed on an unset
// cap, so a programme missing this key blocks redemption for its members.
// `caps.max_points_balance` is deliberately absent: backend2 leaves it null for
// both CMv3 tiers, so its absence is parity, not a gap.

export interface RewardScheduleSeedRow {
  /** Frozen UUIDv7 primary key of the `reward_schedules` row. Never regenerate. */
  readonly id: string;
  readonly programKey: string;
  readonly config: RewardScheduleConfig;
}

const EARN_RULES: RewardScheduleConfig['earn_rules'] = [
  { behavior: 'first_daily_check_in', points: 150 },
  { behavior: 'attended_appointment', points: 2000 },
  { behavior: 'first_attended_appointment', points: 7500 },
];

/** The earn rule only the four non-tobacco substance programmes carry. */
const VERIFIED_TEST_RESULT_RULE: RewardScheduleConfig['earn_rules'][number] = {
  behavior: 'verified_test_result',
  points: 1000,
};

const DAILY_CAPS: NonNullable<RewardScheduleConfig['caps']> = { daily_points_max: 150 };

/** Yearly redemption cap for tobacco and the behavioural programmes (backend2 `tobacco_only` tier value). */
export const TOBACCO_MAX_YEARLY_REDEMPTION = 15000;
/** Yearly redemption cap for the non-tobacco substance programmes (backend2 `non_tobacco_only`). */
export const NON_TOBACCO_MAX_YEARLY_REDEMPTION = 40000;
/** Behavioural programmes (no backend2 tier) take the tobacco cap by product decision. */
export const BEHAVIOURAL_MAX_YEARLY_REDEMPTION = TOBACCO_MAX_YEARLY_REDEMPTION;

function scheduleRow(
  id: string,
  programKey: string,
  maxYearlyRedemption: number,
  extraEarnRules: RewardScheduleConfig['earn_rules'] = [],
): RewardScheduleSeedRow {
  return {
    id,
    programKey,
    config: {
      earn_rules: [...EARN_RULES, ...extraEarnRules].map((rule) => ({ ...rule })),
      caps: { ...DAILY_CAPS },
      max_yearly_redemption: maxYearlyRedemption,
    },
  };
}

export const REWARD_SCHEDULE_SEED: readonly RewardScheduleSeedRow[] = [
  scheduleRow('019f1b00-0001-7a01-8a01-000000000001', 'tobacco', TOBACCO_MAX_YEARLY_REDEMPTION),
  scheduleRow(
    '019f1b00-0002-7a02-8a02-000000000002',
    'alcohol',
    NON_TOBACCO_MAX_YEARLY_REDEMPTION,
    [VERIFIED_TEST_RESULT_RULE],
  ),
  scheduleRow('019f1b00-0003-7a03-8a03-000000000003', 'opioid', NON_TOBACCO_MAX_YEARLY_REDEMPTION, [
    VERIFIED_TEST_RESULT_RULE,
  ]),
  scheduleRow(
    '019f1b00-0004-7a04-8a04-000000000004',
    'cannabis',
    NON_TOBACCO_MAX_YEARLY_REDEMPTION,
    [VERIFIED_TEST_RESULT_RULE],
  ),
  scheduleRow(
    '019f1b00-0005-7a05-8a05-000000000005',
    'stimulants',
    NON_TOBACCO_MAX_YEARLY_REDEMPTION,
    [VERIFIED_TEST_RESULT_RULE],
  ),
  scheduleRow(
    '019f1b00-0006-7a06-8a06-000000000006',
    'gambling',
    BEHAVIOURAL_MAX_YEARLY_REDEMPTION,
  ),
  scheduleRow('019f1b00-0007-7a07-8a07-000000000007', 'gaming', BEHAVIOURAL_MAX_YEARLY_REDEMPTION),
];
